import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Roles, ServiceCategory } from '@prisma/client';
import { capitalize } from '@mui/material';
import { fetchCompleteReportData } from '../../services/reports';
import useUser from '../global/useUser';
import { FilterOption, LoadedUser } from '../../interfaces';
import {
  generateColorForArtist,
  getArtistOptions,
} from '../../services/business';
import { getArtistName } from '../../utils/global/helper';
import { DateType, ReportsReturn, TransactionType } from '../../types/reports';
import { getBusinessTripName } from '../../helpers/businesses';
import { getOrderItemName } from '../../helpers/orders';
import { getInitialFilterValue } from '../../helpers/global';

function useReports() {
  const { user } = useUser<LoadedUser>();

  const availableEmployees = React.useMemo<FilterOption[]>(() => {
    if (user.role === Roles.SHOP_MANAGER) {
      return getArtistOptions(user);
    }
    return [
      {
        label: getArtistName(user.employee),
        value: user.userId,
        color: generateColorForArtist(user.userId, user),
      },
    ];
  }, [user]);

  const tripsOptions = React.useMemo(() => {
    if (!user.business.businessTrips?.length) return [];

    const tempTripOptions = [{ label: 'Home Studio', value: '' }];
    user.business.businessTrips.forEach(trip => {
      tempTripOptions.push({
        label: getBusinessTripName(trip),
        value: trip.tripId,
      });
    });
    return tempTripOptions;
  }, [user]);

  const availableServices = React.useMemo(() => {
    const initialServices: FilterOption[] = [
      {
        label: 'All services',
        value: 'ALL',
      },
      {
        label: 'Tattoos',
        value: ServiceCategory.TATTOO,
      },
    ];

    if (user.business.services?.length) {
      const categories = new Set<ServiceCategory>();
      user.business.services.forEach(s => {
        categories.add(s.serviceCategory as ServiceCategory);
      });
      initialServices.push(
        ...Array.from(categories).map(category => ({
          label: capitalize(category.toLowerCase().replace('_', ' ')),
          value: category,
        })),
      );
    }

    return initialServices;
  }, [user.business.services]);

  const dateTypeOptions: FilterOption[] = React.useMemo(
    () => [
      {
        label: 'Filter by payment date',
        value: 'payment',
      },
      {
        label: 'Filter by appointment date',
        value: 'appointment',
      },
    ],
    [],
  );

  const ordersOptions: FilterOption[] = React.useMemo(
    () => [
      {
        label: 'All transactions',
        value: 'ALL',
      },
      {
        label: 'Appointments only',
        value: 'APPOINTMENTS',
      },
      {
        label: 'Retail checkouts only',
        value: 'ORDERS',
      },
    ],
    [],
  );

  const inactiveEmployeesOptions: FilterOption[] = React.useMemo(
    () => [
      {
        label: 'Include inactive employees',
        value: 'true',
      },
      {
        label: 'Exclude inactive employees',
        value: 'false',
      },
    ],
    [],
  );

  const [reportRange, setReportRange] = React.useState<DateRange<Date>>(() =>
    getInitialFilterValue('ReportsDateRange', [], 'DATE_RANGE'),
  );
  const [service, setService] = React.useState<ServiceCategory>(() =>
    getInitialFilterValue('ReportsServices', availableServices, 'SINGLE'),
  );
  const [dateType, setDateType] = React.useState<DateType>(() =>
    getInitialFilterValue(
      'ReportsDateType',
      dateTypeOptions,
      'SINGLE',
      'payment',
    ),
  );

  const [transactionType, setTransactionType] = React.useState<TransactionType>(
    () =>
      getInitialFilterValue(
        'ReportsTransactionType',
        ordersOptions,
        'SINGLE',
        'ALL',
      ),
  );
  const [selectedEmployees, setSelectedEmployees] = React.useState<string[]>(
    () => getInitialFilterValue('artists', availableEmployees, 'MULTI'),
  );
  const [selectedTrip, setSelectedTrip] = React.useState<string[]>(() =>
    getInitialFilterValue('trips', tripsOptions, 'MULTI'),
  );
  const [includeInactiveEmployees, setIncludeInactiveEmployees] =
    React.useState<boolean>(() =>
      getInitialFilterValue(
        'includeInactiveEmployees',
        inactiveEmployeesOptions,
        'SINGLE',
        !!user.business.inactiveEmployeesToBusinesses?.length,
        true,
      ),
    );

  const isAllArtistsSelected = React.useMemo(
    () => availableEmployees.every(e => selectedEmployees.includes(e.value)),
    [availableEmployees, selectedEmployees],
  );

  const query = useQuery<ReportsReturn>({
    queryKey: [
      'reports',
      {
        businessId: user.business.id,
        startDate: reportRange[0],
        endDate: reportRange[1],
        selectedEmployees,
        dateType,
        service,
        selectedTrip,
        transactionType,
        isAllArtistsSelected,
        includeInactiveEmployees,
      },
    ],
    queryFn: () => {
      if (!reportRange[0] || !reportRange[1] || !selectedEmployees.length)
        return Promise.resolve({ appointments: [], orders: [] });

      const startDate = reportRange[0]!;
      const endDate = reportRange[1]!;

      return fetchCompleteReportData({
        startDate,
        endDate,
        businessId: user.business.id,
        dateType,
        service,
        employeeId: selectedEmployees,
        tripIds: selectedTrip,
        transactionType: transactionType ?? 'ALL',
        isAllArtistsSelected,
        includeInactiveEmployees,
      });
    },
    enabled: !!user,
    // never go stale
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const productOptions: FilterOption[] = React.useMemo(
    () =>
      [
        ...new Set(
          query.data?.orders?.flatMap(order =>
            order.orderItems.map(getOrderItemName),
          ),
        ),
      ].map(name => ({
        label: name || '',
        value: name || '',
      })),
    [query.data],
  );

  const [selectedProducts, setSelectedProducts] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (productOptions.length > 0) {
      const initialProducts = getInitialFilterValue(
        'products',
        productOptions,
        'MULTI',
      );
      setSelectedProducts(initialProducts);
    }
  }, [productOptions]);

  return {
    query,
    state: {
      dateTypeFilter: {
        name: 'ReportsDateType',
        options: dateTypeOptions,
        value: dateType,
        setValue: setDateType,
      },
      servicesFilter: {
        name: 'ReportsServices',
        options: availableServices,
        value: service,
        setValue: setService,
      },
      artistFilter: {
        name: 'artists',
        options: availableEmployees,
        value: selectedEmployees,
        setValue: setSelectedEmployees,
        isAllSelected: isAllArtistsSelected,
      },
      tripFilter: {
        name: 'trips',
        options: tripsOptions,
        value: selectedTrip,
        setValue: setSelectedTrip,
      },
      transactionTypeFilter: {
        name: 'ReportsTransactionType',
        options: ordersOptions,
        value: transactionType,
        setValue: setTransactionType,
      },
      productsFilter: {
        name: 'products',
        options: productOptions,
        value: selectedProducts,
        setValue: setSelectedProducts,
      },
      inactiveEmployeesFilter: {
        name: 'includeInactiveEmployees',
        options: inactiveEmployeesOptions,
        value: includeInactiveEmployees,
        setValue: setIncludeInactiveEmployees,
      },
      reportRange,
      setReportRange,
      showArtistFilter: user.role === Roles.SHOP_MANAGER,
      showTripsFilter: user.business.businessTrips.length > 0,
      showOrdersOptions: user.role === Roles.SHOP_MANAGER,
      showProductsOptions: transactionType !== 'APPOINTMENTS',
      showInactiveEmployeesOption:
        !!user.business.inactiveEmployeesToBusinesses?.length,
    },
  };
}

export default useReports;
